import React, { useState, useEffect } from 'react';
import Header from '../../../components/Header/Header';
import Footer from '../../../components/Footer/Footer';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function UserRole() {
  const [rolesData, setRolesData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [editingRow, setEditingRow] = useState(null);
  const [editFields, setEditFields] = useState({});
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [filters, setFilters] = useState({
    search: '',
    role: '',
    authorization: '',
    indentRights: ''
  });

  // Fetch initial data
  useEffect(() => {
    fetchRolesData();
  }, []);

  const fetchRolesData = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/user/mroles`);
      const data = await response.json();
      if (Array.isArray(data)) {
        setRolesData(data);
        setFilteredData(data);
      } else {
        console.error('Unexpected data format:', data);
      }
    } catch (error) {
      console.error('Error fetching roles data:', error);
    }
  };

  // Filter effect
  useEffect(() => {
    const filtered = rolesData.filter(role => {
      const matchesSearch = filters.search === '' || 
        Object.values(role).some(value => 
          String(value).toLowerCase().includes(filters.search.toLowerCase())
        );
      
      const matchesRole = filters.role === '' || role.Role === filters.role;
      
      const matchesAuthorization = filters.authorization === '' || 
        (filters.authorization === 'Authorized' ? role.isAuthorized === 1 : role.isAuthorized === 0);
      
      const matchesIndentRights = filters.indentRights === '' || 
        role.IndentCreationRights === filters.indentRights;

      return matchesSearch && matchesRole && matchesAuthorization && matchesIndentRights;
    });

    setFilteredData(filtered);
  }, [filters, rolesData]);

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  const handleEdit = (index) => {
    const currentRole = filteredData[index];
    setEditingRow(index);
    // Convert numeric isAuthorized to string format for the form
    setEditFields({
      ...currentRole,
      isAuthorized: currentRole.isAuthorized === 1 ? 'Authorized' : 'Not Authorized'
    });
  };

  const handleFieldChange = (field, value) => {
    setEditFields(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    try {
      const { RoleID, ...updatedFields } = editFields;
      
      // Create a new object for the API request
      const apiData = {
        ...updatedFields,
        // Convert authorization string to number
        isAuthorized: updatedFields.isAuthorized === 'Authorized' ? 1 : 0
      };

      const response = await fetch(`${API_BASE_URL}/admin/user/mroles/${RoleID}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(apiData),
      });

      if (response.ok) {
        // First update the local state to reflect changes immediately
        const updatedRolesData = rolesData.map(role => 
          role.RoleID === RoleID 
            ? { 
                ...role, 
                ...apiData,
                isAuthorized: apiData.isAuthorized // Ensure isAuthorized is properly set
              }
            : role
        );
        setRolesData(updatedRolesData);
        
        // Then fetch fresh data from server to ensure consistency
        await fetchRolesData();
        
        setEditingRow(null);
        // Show success message
        alert('Role updated successfully');
      } else {
        console.error('Error saving data:', await response.text());
        alert('Failed to update role');
      }
    } catch (error) {
      console.error('Error saving data:', error);
      alert('Failed to update role');
    }
  };

  const handlePasswordChange = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/user/change-password/${selectedUser.UserID}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ newPassword }),
      });

      if (response.ok) {
        setShowPasswordModal(false);
        setNewPassword('');
        setSelectedUser(null);
        // Show success message
        alert('Password changed successfully');
      } else {
        const error = await response.text();
        console.error('Error changing password:', error);
        alert('Failed to change password');
      }
    } catch (error) {
      console.error('Error changing password:', error);
      alert('Failed to change password');
    }
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      role: '',
      authorization: '',
      indentRights: ''
    });
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-50 text-gray-800">
      <Header />
      <main className="flex-grow px-4 py-8 w-full">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-800">User Role Management</h1>
          <p className="text-gray-600 mt-2">Manage user roles, permissions, and access rights</p>
        </div>

        {/* Filter Controls */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-8 w-full">
          <h2 className="text-lg font-semibold text-gray-700 mb-4">Filters</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Search Box */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
              <input
                type="text"
                placeholder="Search in all fields..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="w-full p-2 border rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Role Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
              <select
                value={filters.role}
                onChange={(e) => handleFilterChange('role', e.target.value)}
                className="w-full p-2 border rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Roles</option>
                <option value="Project Engineer">Project Engineer</option>
                <option value="PE&SO">PE + SO</option>
                <option value="Location Head">Location Head</option>
                <option value="Director">L2 Director</option>
                <option value="L2 General">L2 General</option>
                <option value="Store Officer">Store Officer</option>
                <option value="Store Head">Store Head</option>
                <option value="Purchase Department">Purchase Department</option>
                <option value="Purchase Head">Purchase Head</option>
                <option value="Normal">Normal</option>
                <option value="admin">Admin</option>
              </select>
            </div>

            {/* Authorization Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Authorization</label>
              <select
                value={filters.authorization}
                onChange={(e) => handleFilterChange('authorization', e.target.value)}
                className="w-full p-2 border rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Authorization</option>
                <option value="Authorized">Authorized</option>
                <option value="Not Authorized">Not Authorized</option>
              </select>
            </div>

            {/* Indent Rights Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Indent Rights</label>
              <select
                value={filters.indentRights}
                onChange={(e) => handleFilterChange('indentRights', e.target.value)}
                className="w-full p-2 border rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Indent Rights</option>
                <option value="Normal">Normal</option>
                <option value="Special">Special</option>
                <option value="Emergency">Emergency</option>
              </select>
            </div>
          </div>

          {/* Filter Stats and Clear Button */}
          <div className="flex justify-between items-center mt-4">
            <div className="text-sm text-gray-600">
              Showing {filteredData.length} of {rolesData.length} entries
            </div>
            <button
              onClick={clearFilters}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition flex items-center"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Clear Filters
            </button>
          </div>
        </div>

        {/* Roles Table */}
        <div className="bg-white rounded-lg shadow-sm w-full">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role ID</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User ID</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Username</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Designation</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Indent Rights</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Authorization</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredData.map((role, index) => (
                  <tr key={role.RoleID} className={editingRow === index ? 'bg-yellow-50' : 'hover:bg-gray-50'}>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{role.RoleID}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{role.UserID}</td>
                    {editingRow === index ? (
                      <>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <input
                            type="text"
                            value={editFields.Username}
                            onChange={(e) => handleFieldChange('Username', e.target.value)}
                            className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                          />
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <input
                            type="text"
                            value={editFields.Name}
                            onChange={(e) => handleFieldChange('Name', e.target.value)}
                            className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                          />
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <input
                            type="text"
                            value={editFields.Designation}
                            onChange={(e) => handleFieldChange('Designation', e.target.value)}
                            className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                          />
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <select
                            value={editFields.Role}
                            onChange={(e) => handleFieldChange('Role', e.target.value)}
                            className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="Project Engineer">Project Engineer</option>
                            <option value="PE&SO">PE + SO</option>
                            <option value="Location Head">Location Head</option>
                            <option value="Director">L2 Director</option>
                            <option value="L2 General">L2 General</option>
                            <option value="Store Officer">Store Officer</option>
                            <option value="Store Head">Store Head</option>
                            <option value="Purchase Department">Purchase Department</option>
                            <option value="Purchase Head">Purchase Head</option>
                            <option value="Normal">Normal</option>
                            <option value="admin">Admin</option>
                          </select>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <input
                            type="email"
                            value={editFields.Email}
                            onChange={(e) => handleFieldChange('Email', e.target.value)}
                            className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                          />
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <select
                            value={editFields.IndentCreationRights}
                            onChange={(e) => handleFieldChange('IndentCreationRights', e.target.value)}
                            className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="Normal">Normal</option>
                            <option value="Special">Special</option>
                            <option value="Emergency">Emergency</option>
                          </select>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <select
                            value={editFields.isAuthorized}
                            onChange={(e) => handleFieldChange('isAuthorized', e.target.value)}
                            className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="Authorized">Authorized</option>
                            <option value="Not Authorized">Not Authorized</option>
                          </select>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{role.Username}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{role.Name}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{role.Designation}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{role.Role}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{role.Email}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{role.IndentCreationRights}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                            role.isAuthorized === 1 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {role.isAuthorized === 1 ? 'Authorized' : 'Not Authorized'}
                          </span>
                        </td>
                      </>
                    )}
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex space-x-2">
                        {editingRow === index ? (
                          <button
                            onClick={handleSave}
                            className="px-3 py-1 bg-green-500 text-white rounded-lg hover:bg-green-600 transition flex items-center"
                          >
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                            </svg>
                            Save
                          </button>
                        ) : (
                          <>
                            <button
                              onClick={() => handleEdit(index)}
                              className="px-3 py-1 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition flex items-center"
                            >
                              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                              Edit
                            </button>
                            <button
                              onClick={() => {
                                setSelectedUser(role);
                                setShowPasswordModal(true);
                              }}
                              className="px-3 py-1 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition flex items-center"
                            >
                              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                              </svg>
                              Change Password
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Password Change Modal */}
        {showPasswordModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
            <div className="bg-white p-6 rounded-lg shadow-xl w-96">
              <h2 className="text-xl font-semibold mb-4">Change Password</h2>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  New Password
                </label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter new password"
                />
              </div>
              <div className="flex justify-end space-x-2">
                <button
                  onClick={() => {
                    setShowPasswordModal(false);
                    setNewPassword('');
                    setSelectedUser(null);
                  }}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handlePasswordChange}
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition"
                >
                  Change Password
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}

export default UserRole;